import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import type { StudyDraft } from '@/model/Draft.model';
import { ApiService } from '@/support/ApiService';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute, api as apiRoutes, web as webRoutes } from '@/support/route';
import type { AxiosError, AxiosResponse } from 'axios';
import InVivoError from '@/model/InVivoError';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { StudyCreateProvider } from '@/utils/studyCreation/StudyCreateProvider.tsx';
import { useStudyCreateSave } from '@/utils/studyCreation/useStudyCreate.tsx';
import { encodeGroupMultiSelectMetadata } from './DraftUtils';

interface DraftProps {
  page: string;
}

interface ComponentProps extends DraftProps {
  draft?: StudyDraft;
}

const DraftWrapper: React.FC<{ draft?: StudyDraft; children: React.ReactNode }> = ({ draft, children }) => {
  const { setDraftId } = useStudyCreateSave();

  useEffect(() => {
    if (draft?.id) {
      setDraftId(draft.id);
    }
  }, [draft?.id, setDraftId]);

  return <>{children}</>;
};

const withDraft = (Component: React.FC<ComponentProps>) => (props: DraftProps) => {
  const [loadingDraft, setLoadingDraft] = useState<boolean>(true);
  const [apiError, setApiError] = useState<AxiosResponse<StudyDraft> | undefined | false>(false);
  const [draft, setDraft] = useState<StudyDraft | undefined>(undefined);
  const [draftError, setDraftError] = useState<AxiosError | false>(false);
  const location = useLocation();
  const history = useHistory();
  const draftId = new URLSearchParams(location.search).get('draftId');
  const isMounted = useMountedState();

  const fetchDraft = async () => {
    try {
      const {
        data: { data: draft },
      }: {
        data: {
          data: StudyDraft;
        };
      } = await Http.get(apiRoute('drafts.studies.get', { id: draftId }));
      if (draft?.form_data?.project_id) {
        const {
          data: { data: project },
        } = await Http.get(apiRoute('projects.get', { projectId: draft.form_data.project_id }));
        if (project) {
          draft.selectedProjectName = project.name;
        }
      }
      if (draft?.form_data?.study_code_id) {
        const studyCodeApiId = draft.form_data.study_code_id;
        try {
          const code = await ApiService.call({
            endpoint: 'GET /api/v1/team/study-codes/{studyCodeApiId}',
            path: { studyCodeApiId },
            options: {
              onError: { toast: false, throw: true },
            },
          });

          if (code.type === 'success') {
            draft.selectedStudyCode = code.body;
          }
        } catch (ex) {
          if (isMounted()) {
            const error = ex as InVivoError;
            if (error.context?.ApiStatusCode === '404') {
              // Study code no longer exists - clear the reference gracefully
              draft.selectedStudyCode = undefined;
              draft.form_data.study_code_id = undefined;
              // Don't set API error - this is expected behavior for deleted entities
            } else {
              // Only set API error for unexpected errors
              // Extract the original AxiosResponse from the InVivoError cause
              const axiosError = error.cause as AxiosError<StudyDraft>;
              setApiError(axiosError?.response);
            }
          }
        }
      }

      const { data: fileData } = await Http.get(apiRoute('drafts.files', { id: draftId }));

      draft.form_data.files = fileData;

      // Workaround to handle old drafts that may have non encoded multi select group metas
      encodeGroupMultiSelectMetadata(draft);

      setDraft(draft);
    } catch (error) {
      if ((error as AxiosError).response?.status === 409) {
        setDraftError(error as AxiosError);
      } else {
        setApiError((error as AxiosError<StudyDraft>).response);
      }
    } finally {
      setLoadingDraft(false);
    }
  };

  const handleDraftsDelete = async () => {
    try {
      await Http.delete(apiRoutes('drafts.studies.delete', { id: draftId }));
    } catch (error) {
      setApiError((error as AxiosError<StudyDraft>).response);
    } finally {
      history.push(webRoutes('dashboard'));
    }
  };

  useEffect(() => {
    if (draftId) {
      fetchDraft();
    } else {
      setLoadingDraft(false);
    }
  }, [draftId]);

  return (
    <>
      {draftError && (
        <ApiErrorBanner
          className="ml3"
          title="This draft is invalid"
          // @ts-expect-error: mapped from ts-ignore
          text={draftError.response?.data.message ?? 'Please restart study creation process'}
          altButtonFunc={() => handleDraftsDelete()}
          altButtonText="Delete draft"
        />
      )}
      {apiError && <ApiErrorBanner error={apiError} />}
      {draftId && loadingDraft && (
        <div className="w-100 h-100">
          <Loading txt="Fetching draft..." />
        </div>
      )}
      {!loadingDraft && !draftError && (
        <StudyCreateProvider>
          <DraftWrapper draft={draft}>
            <Component draft={draft} {...props} />
          </DraftWrapper>
        </StudyCreateProvider>
      )}
    </>
  );
};

export default withDraft;
